import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

type ThemeMode = 'light' | 'dark' | 'system'
type ResolvedTheme = 'light' | 'dark'

export const useLayoutStore = defineStore('layout', () => {
  // State
  const userDrawerOpen = ref(false)
  const themeMode = ref<ThemeMode>('system')
  const systemPreference = ref<ResolvedTheme>('light')

  // Initialize system preference
  const updateSystemPreference = () => {
    systemPreference.value = window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light'
  }
  updateSystemPreference()

  // Listen for system theme changes
  window
    .matchMedia('(prefers-color-scheme: dark)')
    .addEventListener('change', updateSystemPreference)

  // Get saved theme from localStorage on init
  const savedThemeMode = localStorage.getItem('app-theme-mode') as ThemeMode | null
  if (savedThemeMode && ['light', 'dark', 'system'].includes(savedThemeMode)) {
    themeMode.value = savedThemeMode
  }

  // Getters
  const theme = computed<ResolvedTheme>(() => {
    if (themeMode.value === 'system') {
      return systemPreference.value
    }
    return themeMode.value as ResolvedTheme
  })

  const isDark = computed(() => theme.value === 'dark')

  // Actions
  const toggleUserDrawer = () => {
    userDrawerOpen.value = !userDrawerOpen.value
  }

  const openUserDrawer = () => {
    userDrawerOpen.value = true
  }

  const closeUserDrawer = () => {
    userDrawerOpen.value = false
  }

  const toggleTheme = () => {
    // Cycle through: light -> dark -> system -> light
    const modes: ThemeMode[] = ['light', 'dark', 'system']
    const currentIndex = modes.indexOf(themeMode.value)
    const nextIndex = (currentIndex + 1) % modes.length
    themeMode.value = modes[nextIndex]
    localStorage.setItem('app-theme-mode', themeMode.value)
  }

  const setTheme = (newThemeMode: ThemeMode) => {
    themeMode.value = newThemeMode
    localStorage.setItem('app-theme-mode', newThemeMode)
  }

  // Watch for theme changes to update localStorage
  watch(themeMode, (newMode) => {
    localStorage.setItem('app-theme-mode', newMode)
  })

  return {
    // State
    userDrawerOpen,
    theme,
    themeMode,

    // Getters
    isDark,

    // Actions
    toggleUserDrawer,
    openUserDrawer,
    closeUserDrawer,
    toggleTheme,
    setTheme,
  }
})
